import { types as t, flow, destroy, getParent } from 'mobx-state-tree';

import { sortBy } from 'helpers';

import { LoadingState } from 'models/ui';
import { User } from 'models/users';
import { withEnv } from 'models/withEnv';

export const filterUsers = (users, search) => {
  const str = search.toLowerCase();
  return users.filter((user) => {
    const name = user.name.toLowerCase();
    const role = user.role.toLowerCase().replace('user', 'employee');
    const email = user.email.toLowerCase();
    const department = user.departments
      ? user.departments
          .map((y) => y.translations.map((x) => x.title))
          .flat()
          .join(', ')
          .toLowerCase()
      : '';
    return (
      name.includes(str) || email.includes(str) || role.includes(str) || department.includes(str)
    );
  });
};

export const UserStore = t
  .model('UserStore', {
    items: t.map(User),
    loading: false,
    loaded: false,
    followerStatsState: LoadingState,
    editing: t.maybeNull(User),
  })
  .views(withEnv)
  .views((self) => ({
    get sorted() {
      return sortBy(self.filteredUsers.slice(), self.settings.sortBy, self.settings.sortType);
    },
    get usersForTab() {
      switch (self.settings.chosenTab) {
        case 1:
          return self.superadmins;
        case 2:
          return self.admins;
        case 3:
          return self.recruiters;
        case 4:
          return self.limited;
        case 5:
          return self.employees;
        default:
          return self.sorted;
      }
    },
    get settings() {
      return getParent(self).uiSettings.team;
    },
    get superadmins() {
      return self.sorted.filter((u) => u.role === 'superadmin');
    },
    get admins() {
      return self.sorted.filter((u) => u.role === 'admin');
    },
    get recruiters() {
      return self.sorted.filter((u) => u.role === 'recruiter');
    },
    get limited() {
      return self.sorted.filter((u) => u.role === 'limited');
    },
    get employees() {
      return self.sorted.filter((u) => u.role === 'user');
    },
    get nonEmployees() {
      return self.sorted.filter((u) => u.role !== 'user');
    },
    get filteredUsers() {
      return filterUsers(self.list, self.settings.searchStr);
    },
    get list() {
      return Array.from(self.items.values()).filter((u) => u.isActive && !u.isSystemUser);
    },
  }))
  .actions((self) => ({
    getTwoFactorInfo: flow(function* () {
      const res = yield self.env.api.users.twoFactor();
      return res;
    }),
    setTwoFactor: flow(function* (code, phone) {
      const data = { code, phone };
      const res = yield self.env.api.users.setTwoFactor(data);
      return res;
    }),
    removeAuthenticator: flow(function* (password) {
      return yield self.env.api.users.removeAuthenticator({ password });
    }),
    addAuthenticator: flow(function* (password, code) {
      return yield self.env.api.users.addAuthenticator({ password, code });
    }),
    removeSMSAuthenticator: flow(function* (password) {
      return yield self.env.api.users.removeSMSAuthenticator({ password });
    }),
    addSMSNumber: flow(function* (password, phone) {
      return yield self.env.api.users.addSMSNumber({ password, phone });
    }),
    addSMSAuthenticator: flow(function* (password, code) {
      return yield self.env.api.users.addSMSAuthenticator({ password, code });
    }),
    loadFollowerStats: flow(function* () {
      if (self.followerStatsState !== 'init') {
        return;
      }

      self.followerStatsState = 'loading';
      const res = yield self.env.api.users.loadFollowingStats();
      if (!res.error) {
        res.data.forEach((user) => {
          const u = self.items.get(user.id);
          if (user.followingJobs.length) {
            u.setFollowingJobs(user.followingJobs);
          }
          if (user.followingCandidates.length) {
            u.setFollowingCandidates(user.followingCandidates);
          }
        });
        self.followerStatsState = 'loaded';
      } else {
        self.followerStatsState = 'error';
      }
    }),
    load: flow(function* (refetch = false) {
      if ((self.loaded || self.loading) && !refetch) return;
      self.loading = true;

      const res = yield self.env.api.users.load();
      if (!res.error) {
        res.data.results.forEach((d) => {
          if (self.items.has(d.id)) {
            self.items.put({
              ...self.items.get(d.id),
              ...d,
            });
          } else {
            self.items.put(d);
          }
        });
      }

      self.loaded = true;
      self.loading = false;

      self.env.api.users.loadPictures().then((res) => {
        res.data.forEach((u, index) => {
          setTimeout(() => {
            self.items.get(u.id).setPicture(u.picture);
          }, index * 2);
        });
      });
    }),
    loadIndividual: flow(function* (id) {
      const res = yield self.env.api.users.loadUser(id);
      if (!res.error) {
        self.add(res.data);
      }
    }),
    setEditing(user) {
      self.editing = user;
    },
    clearEditing() {
      destroy(self.editing);
    },
    add(user) {
      if (!self.items.has(user.id)) {
        self.items.put(user);
      }
      return self.items.get(user.id);
    },
    delete: flow(function* (user) {
      const res = yield self.env.api.users.remove(user.id);
      if (!res.error) {
        user.isActive = false;
        self.env.showSuccess(self.env.t('userstore.delete.success'));
        return true;
      } else {
        self.env.showSuccess(self.env.t('userstore.delete.error'));
        return false;
      }
    }),
  }));
